import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase";

export interface Address {
  country: string;
  state: string;
  city: string;
  firstLine: string;
  postCode: string;
}

export interface DirectBankTransfer {
  type: "directBankTransfer";
  fullName: string;
  currency: string;
  iban?: string;
  accountNumber?: string;
  routingNumber?: string;
  accountType?: string;
  address: Address;
}

export interface Paypal {
  type: "paypal";
  email: string;
  currency: string;
}

export interface MpesaTransfer {
  type: "mpesaTransfer";
  phoneNumber: string;
  paybillNumber?: string;
  accountNumber?: string;
  tillNumber?: string;
}


export type PayoutMethod = DirectBankTransfer | Paypal | MpesaTransfer;

// ✅ UPSERT
export const upsertPayoutMethod = createAsyncThunk(
  "payoutDetails/upsertPayoutMethod",
  async ({ userId, method }: { userId: string; method: PayoutMethod }) => {
    const ref = doc(db, "payoutDetails", userId);
    const docSnap = await getDoc(ref);

    if (docSnap.exists()) {
      await updateDoc(ref, {
        methods: arrayUnion(method),
      });
    } else {
      await setDoc(ref, {
        methods: [method],
      });
    }
    return { userId, method };
  }
);

// ✅ FETCH
export const fetchPayoutMethods = createAsyncThunk(
  "payoutDetails/fetchPayoutMethods",
  async (userId: string) => {
    const ref = doc(db, "payoutDetails", userId);
    const docSnap = await getDoc(ref);
    if (!docSnap.exists()) return [];
    return docSnap.data().methods as PayoutMethod[];
  }
);

// ✅ DELETE BY TYPE
export const deletePayoutMethod = createAsyncThunk(
  "payoutDetails/deletePayoutMethod",
  async ({ userId, type }: { userId: string; type: string }) => {
    const ref = doc(db, "payoutDetails", userId);
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(ref);
      if (!snapshot.exists()) return;

      const current = snapshot.data().methods || [];
      const updated = current.filter((m: PayoutMethod) => m.type !== type);
      transaction.update(ref, { methods: updated });
    });
    return { userId, type };
  }
);

const payoutDetailsSlice = createSlice({
  name: "payoutDetails",
  initialState: {
    loading: false,
    error: null as string | null,
    methods: [] as PayoutMethod[],
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // UPSERT
      .addCase(upsertPayoutMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upsertPayoutMethod.fulfilled, (state, action) => {
        state.loading = false;
        const newMethod = action.payload.method;
        state.methods = [
          ...state.methods.filter((m) => m.type !== newMethod.type),
          newMethod,
        ];
      })
      .addCase(upsertPayoutMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to save payout method";
      })

      // FETCH
      .addCase(fetchPayoutMethods.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayoutMethods.fulfilled, (state, action) => {
        state.loading = false;
        state.methods = action.payload;
      })
      .addCase(fetchPayoutMethods.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch payout methods";
      })

      // DELETE
      .addCase(deletePayoutMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePayoutMethod.fulfilled, (state, action) => {
        state.loading = false;
        state.methods = state.methods.filter(
          (method) => method.type !== action.payload.type
        );
      })
      .addCase(deletePayoutMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete payout method";
      });
  },
});

export default payoutDetailsSlice.reducer;
